import { useEffect, useState } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { doc, onSnapshot } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { INITIAL_ESSENCE } from '@spirits/shared';
import { db, ensureAnonymousAuth, functions } from './firebase';
import { useGameStore } from './store/useGameStore';
import LobbyRoute from './routes/LobbyRoute';
import PublicRunsRoute from './routes/PublicRunsRoute';
import PlayRoute from './routes/PlayRoute';
import SharedRecapRoute from './routes/SharedRecapRoute';
import AdminRoute from './admin/AdminRoute';

const bootstrapUser = httpsCallable<
  unknown,
  { ok: boolean; playerName: string; created: boolean; chatModel?: string; evalModel?: string }
>(functions, 'bootstrapUser');

export default function App() {
  const setUser = useGameStore((s) => s.setUser);
  const setChatModel = useGameStore((s) => s.setChatModel);
  const setEvalModel = useGameStore((s) => s.setEvalModel);
  const updatePlayerName = useGameStore((s) => s.updatePlayerName);
  const updateEssence = useGameStore((s) => s.updateEssence);
  const [bootError, setBootError] = useState<string | null>(null);

  useEffect(() => {
    let unsubUserDoc: (() => void) | null = null;

    ensureAnonymousAuth()
      .then(async (u) => {
        setUser({
          uid: u.uid,
          displayName: 'Anonymous Player',
          playerName: 'Anonymous Player',
          essence: INITIAL_ESSENCE,
        });

        try {
          const res = await bootstrapUser();
          if (res.data?.playerName) updatePlayerName(res.data.playerName);
          if (res.data?.chatModel) setChatModel(res.data.chatModel);
          if (res.data?.evalModel) setEvalModel(res.data.evalModel);
        } catch (e) {
          console.warn('[boot] bootstrapUser failed', e);
        }

        unsubUserDoc = onSnapshot(doc(db, 'users', u.uid), (snap) => {
          const data = snap.data() as { playerName?: string; essence?: number } | undefined;
          if (data?.playerName) updatePlayerName(data.playerName);
          if (typeof data?.essence === 'number') updateEssence(data.essence);
        });
      })
      .catch((e) => setBootError(String(e?.message ?? e)));

    return () => {
      if (unsubUserDoc) unsubUserDoc();
    };
  }, [setUser, setChatModel, setEvalModel, updatePlayerName, updateEssence]);

  return (
    <div className="min-h-full">
      {bootError && (
        <div className="fixed top-3 right-3 z-50 max-w-sm rounded-lg bg-rose-500/20 border border-rose-500/40 text-slate-100 px-3 py-2 text-sm">
          <strong>Boot error:</strong> {bootError}
        </div>
      )}
      <Routes>
        <Route path="/" element={<LobbyRoute />} />
        <Route path="/public" element={<PublicRunsRoute />} />
        <Route path="/play/:storyId" element={<PlayRoute />} />
        <Route path="/s/:storyId" element={<SharedRecapRoute />} />
        <Route path="/admin/*" element={<AdminRoute />} />
        <Route path="*" element={<Navigate to="/" replace />} />
      </Routes>
    </div>
  );
}
